import {getEnvironment} from '../environment';
import {axiosInstance, RequestConfig} from '../axios';
import {expandUrlTemplate} from '../utilities';

/**
 * Get an application preference
 *
 * @param preferenceKey A string that represents the key to use to lookup the preference
 * @param config An optional axios request configuration
 *
 * @returns Returns the AxiosPromise with the data if successful
 */
export const getPreference = (preferenceKey: string, config?: RequestConfig): PreferencePromise<string> => {
   const preferencesApiUrl = getEnvironment().preferencesApiUrl;

   if (preferencesApiUrl === undefined) {
      return Promise.reject("The preferences api url is not set in the unifocus application environment");
   }

   const expandedPreferenceApiUrl = expandUrlTemplate(
      preferencesApiUrl,
      {
         module: getEnvironment().applicationId,
         key: preferenceKey
      }
   );

   return axiosInstance.get(expandedPreferenceApiUrl, {
      transformResponse: [(data) => { return data; }], // https://github.com/axios/axios/issues/907
      ...config
   } as RequestConfig);
};

/**
 * Saves application preference.
 *
 * @param preferenceKey A string that represents the key to use to store the preference
 * @param preferenceValue A string that represents the preference value.  This can be a stringified
 *                        JSON object
 * @param config An optional axios request configuration
 *
 * @returns Returns the AxiosPromise
 */
export const savePreference = (preferenceKey: string, preferenceValue: string, config?: RequestConfig): PreferencePromise<any> => {
   const preferencesApiUrl = getEnvironment().preferencesApiUrl;

   if (preferencesApiUrl === undefined) {
      return Promise.reject("The preferences api url is not set in the unifocus application environment");
   }

   const expandedPreferenceApiUrl = expandUrlTemplate(
      preferencesApiUrl,
      {
         module: getEnvironment().applicationId,
         key: preferenceKey
      }
   );

   return axiosInstance.put(expandedPreferenceApiUrl, preferenceValue, {
      transformRequest: [(data) => { return data; }], // https://github.com/axios/axios/issues/907
      ...config
   } as RequestConfig);
};

export interface PreferenceResponse<T = any>  {
   data: T;
   status: number;
   statusText: string;
   headers: any;
   request?: any;
}

export interface PreferencePromise<T = any> extends Promise<PreferenceResponse<T>> {
}
